import { FC, useEffect } from 'react';

import { getMimetypeFromFilename } from '@bonusly/common-action-input';
import { Box } from '@bonusly/design-system';

import { useNetworkConditionsStore } from '../hooks/use-network-conditions-store';

interface VideoSourceProps {
	videoHdSource: string;
	videoSdSource: string;
	videoOriginalSource: string;
	videoTranscriptSource?: string;
	videoRef?: HTMLVideoElement | null;
}

export const VideoSourceElements: FC<VideoSourceProps> = ({ videoHdSource, videoSdSource, videoOriginalSource, videoTranscriptSource, videoRef }) => {
	const hdCapable = useNetworkConditionsStore((state) => state.hdCapable);

	// Explicitly load the video when sources are set
	useEffect(() => {
		if (videoRef) {
			videoRef.load();
		}
	}, [videoRef, videoHdSource, videoSdSource, videoOriginalSource]);

	// Browsers can struggle with mimetypes when codecs are a mismatch
	// so, we'll always provide a mp4 source option as this can solve the issue
	// this allows Chrome, for instance, to play an mov file without issue.
	const videoHdSourceElement = hdCapable && videoHdSource && (
		<source data-target="hd" src={videoHdSource} {...(getMimetypeFromFilename(videoHdSource) && { type: getMimetypeFromFilename(videoHdSource) as string })} />
	);

	const videoSdSourceElement = videoSdSource && (
		<source data-target="sd" src={videoSdSource} {...(getMimetypeFromFilename(videoSdSource) && { type: getMimetypeFromFilename(videoSdSource) as string })} />
	);

	const videoOriginalSourceElement = videoOriginalSource && (
		<source
			data-target="original"
			src={videoOriginalSource}
			{...(getMimetypeFromFilename(videoOriginalSource) && { type: getMimetypeFromFilename(videoOriginalSource) as string })}
		/>
	);

	return (
		<>
			{videoHdSourceElement}
			{videoSdSourceElement}
			{videoOriginalSourceElement}
			{videoTranscriptSource && <Box as="track" label="Transcript" src={videoTranscriptSource} kind="subtitles" default />}
		</>
	);
};
